import React, { FC, useContext, useEffect, useState } from 'react';
import { useFocusManager } from '@react-aria/focus';
import ExistingList from 'Components/SmartList/ExistingList';
import UserDetailsContext from 'Context/userDetailsContext';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery, useMutation } from '@apollo/react-hooks';
import { toastMessage } from 'Containers/Toast/actions';
import { useDispatch } from 'react-redux';
import { Loading } from 'Components/Loading';
import { FormattedDate } from 'Utils/formattedDate';
import { numberWithCommas } from 'Utils/numberWithCommas';
import { setNewListValue } from 'Containers/Filter/ListMembership/actions/listMembership.actions';
import withErrorBoundary from 'HOC/ErrorBoundary';
import { leadListGql, updateListGql, appendUsersToListGql } from './gqlQuery';

interface Props {
  checkedItems?: any;
  userIds: any[];
  closeModal: () => void;
  filters?: any;
  searchText?: string;
}

const DEFAULT_SORTING_COLUMN = 'lastModified';
const DEFAULT_SORTING_DIRECTION = 'desc';

// Need to have this info
const mapColumnsToKeys = (column: string, direction?: string) => {
  switch (column) {
    case 'name':
      return `name:${direction}`;
    case 'leads':
      return `count:${direction}`;
    case 'lastModified':
      return `updatedAt:${direction}`;
    default:
      return '';
  }
};

const ExistingListContainer: FC<Props> = ({
  checkedItems,
  userIds,
  closeModal,
  filters,
  searchText,
}: Props) => {
  const Dispatch = useDispatch();
  const userDetails = useContext(UserDetailsContext);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedList, setSelectedList] = useState('');

  const [sortColumnObject, setSortColumnObject] = useState({
    column: DEFAULT_SORTING_COLUMN,
    direction: DEFAULT_SORTING_DIRECTION,
  });

  const columns = ['name', 'leads', 'lastModified'];
  const allColumns = ['name', 'leads', 'lastModified'];

  const profiles = [];

  checkedItems.forEach((item: string, idx: number) =>
    profiles.push({ userkey: item, id: userIds[idx] })
  );

  // All Queries //
  const { loading: listLoading, data: listData } = useQuery(leadListGql, {
    notifyOnNetworkStatusChange: true,
    variables: {
      inst_id: userDetails.instId,
      account_id: userDetails.accountId,
      sort: mapColumnsToKeys(
        sortColumnObject.column,
        sortColumnObject.direction
      ),
      size: 5,
      page,
      search: `name::${searchTerm}`,
      myListsOnly: true,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data: any) => {
      const list = [...data?.paginatedList?.list].map((item: any) => {
        // const clonedItem = {...item};
        // item.lastModified = FormattedDate(item.lastModified);
        return { ...item, lastModified: FormattedDate(item.lastModified) };
      });
      setSelectedList(list.length > 0 ? list[0] : '');
    },
  });

  const [appendUsers, { loading: appendUsersLoading, data: appendUsersData }] =
    useMutation(appendUsersToListGql, {
      onCompleted: (data) => {
        Dispatch(
          toastMessage({
            message: `Added ${numberWithCommas(
              data.listAudienceAppendIds.rows_modified
            )} Inquiries to <b>${data.listAudienceAppendIds.data.name}</b>`,
            position: 'bottom',
          })
        );
        if (closeModal) closeModal();
      },
    });

  const [
    updateList,
    { loading: updateLoading, error: updateError, data: updateData },
  ] = useMutation(updateListGql);
  // End Queries //

  // This fixes focus getting out of Modal Dialog when user switches between "Existing List" and "Create New",
  // while using screen reader (ChromeVox). It doesn't happen if we don't use screen reader.
  const focusManager = useFocusManager();
  useEffect(() => {
    if (focusManager) {
      focusManager.focusFirst();
    }
  }, [focusManager]);

  const onSearch = (e: any) => {
    setPage(1);
    setSearchTerm(e.target.value.length >= 3 ? e.target.value : '');
  };
  const onClickSortHandler = (column: string) => {
    let newSortingDirection;
    if (column === sortColumnObject.column) {
      newSortingDirection =
        sortColumnObject.direction === 'desc' ? 'asc' : 'desc';
    } else {
      newSortingDirection = 'desc';
    }
    setSortColumnObject({
      column,
      direction: newSortingDirection,
    });
  };
  const pagingPrevPage = () => {
    setPage(page <= 1 ? listData.paginatedList.totalPages : page - 1);
  };
  const pagingNextPage = () => {
    setPage(page >= listData.paginatedList.totalPages ? 1 : page + 1);
  };

  const SelectedListSet = (val: any) => {
    setSelectedList(val);
  };
  const onSaveHandler = (e: any) => {
    const variables = {
      filters: checkedItems.length > 0 ? null : filters.filtersv2,
      inst_id: userDetails.instId,
      account_id: userDetails.accountId,
      id: e.selection.userKeyConsolidated,
      audiencesSearch: checkedItems.length > 0 ? null : searchText,
    };

    if (!e.overwrite) {
      // Rename a list only //
      // As the variable name needs to be passed and is different, depending on whether it's an overwrite
      // or a Rename, I had to use the promise syntax.
      const { renameValue } = e;
      // @ts-ignore
      variables.name = renameValue.length > 0 ? renameValue : '';
      updateList({
        variables,
      }).then(({ data }) => {
        if (checkedItems.length > 0) {
          appendUsers({
            variables: {
              inst_id: userDetails.instId,
              account_id: userDetails.accountId,
              id: data.listUpdate.data.id,
              profiles,
            },
          });
        } else {
          Dispatch(
            toastMessage({
              message: `Added ${numberWithCommas(
                data.listUpdate.rows_modified
              )} Inquiries to <b>${data.listUpdate.data.name}</b>`,
              position: 'bottom',
            })
          );
          if (closeModal) closeModal();
        }
      });
    } else if (e.renameValue) {
      const { renameValue } = e;
      // @ts-ignore
      variables.name = renameValue.length > 0 ? renameValue : '';
      // Overwriting list //
      // @ts-ignore
      variables.over_write = true;

      updateList({
        variables,
      }).then(({ data }) => {
        if (checkedItems.length > 0) {
          appendUsers({
            variables: {
              inst_id: userDetails.instId,
              account_id: userDetails.accountId,
              id: data.listUpdate.data.id,
              profiles,
            },
          });
        } else {
          Dispatch(
            toastMessage({
              message: `Added ${numberWithCommas(
                data.listUpdate.data.count
              )} Inquiries to <b>${data.listUpdate.data.name}</b>`,
              position: 'bottom',
            })
          );
          if (closeModal) closeModal();
        }
      });
    } else {
      // Overwriting list //
      // As the variable name needs to be passed and is different, depending on whether it's an overwrite
      // or a Rename, I had to use the promise syntax.
      const { name } = listData.paginatedList.list.find(
        (entry: any) =>
          entry.userKeyConsolidated === e.selection.userKeyConsolidated
      );
      // @ts-ignore
      variables.over_write = true;
      // @ts-ignore
      variables.name = name;

      updateList({
        variables,
      }).then(({ data }) => {
        if (checkedItems.length > 0) {
          appendUsers({
            variables: {
              inst_id: userDetails.instId,
              account_id: userDetails.accountId,
              id: data.listUpdate.data.id,
              profiles,
            },
          });
        } else {
          Dispatch(
            toastMessage({
              message: `Added ${numberWithCommas(
                data.listUpdate.data.count
              )} Inquiries to <b>${data.listUpdate.data.name}</b>`,
              position: 'bottom',
            })
          );
          if (closeModal) closeModal();
        }
      });
    }

    // Alert the Filter that a list name was changed so that it can refetch
    setNewListValue(Dispatch, true);
  };

  if (listData && listData.paginatedList) {
    return (
      <ExistingList
        disabled={
          updateData ||
          appendUsersData ||
          updateData ||
          updateLoading ||
          appendUsersLoading ||
          updateLoading
        }
        loading={listLoading}
        sort={{
          activeColumn: {
            name: sortColumnObject.column,
            direction: sortColumnObject.direction,
          },
          events: {
            onClickSortHandler,
          },
        }}
        listData={listData.paginatedList}
        columns={columns}
        allColumns={allColumns}
        closeModal={closeModal}
        page={listData.paginatedList.page}
        recordsPerPage={5}
        pagingTotalRecords={listData.paginatedList.totalRecords}
        pagingPrevPage={pagingPrevPage}
        pagingNextPage={pagingNextPage}
        onCreate={onSaveHandler}
        selectedList={selectedList}
        setSelectedList={SelectedListSet}
        onSearch={onSearch}
        errorMessage={
          updateError &&
          JSON.parse(updateError.networkError.result.errors[0].message)
            .serverResponse.message
        }
        checkedItems={checkedItems}
        totalPages={listData.paginatedList.totalPages}
      />
    );
  }
  return <Loading />;
};

ExistingListContainer.defaultProps = {
  checkedItems: [],
  filters: {},
  searchText: '',
};
export default withErrorBoundary(ExistingListContainer);
